/**
 * Trader Dashboard Page
 *
 * Displays running strategies for a specific trader in a list view.
 * Shows position details including prices, liquidation, funding rate, and profit.
 * Accessible at /user/:trader_name
 *
 * Color Theme: Professional Trading Terminal
 * - Consistent, readable colors optimized for long trading sessions
 * - Green/Red for profit/loss only
 * - Amber for prices (Bloomberg-style)
 * - Blue for spot, Purple for futures distinction
 *
 * New Position Execution Feature:
 * - Displays real-time spot-futures price differentials using ORDERBOOK prices
 * - Shows Binance Futures funding rates with countdown timers
 * - Execute StandbyHedged strategies directly from the dashboard
 */

import React, { useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Avatar,
  Tooltip,
  Stack,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";

import {
  TraderDataProvider,
  useTraderData,
} from "../contexts/TraderDataContextLegacy";
import { NewPositionDataProvider } from "../contexts/NewPositionDataContext";
import type { PositionOpportunity } from "../contexts/NewPositionDataContext";
import type { StrategyInfo, ProfitTier } from "../types/strategy";
import { HeaderStats, HeaderStatsInline } from "../components/HeaderStats";
import { AssetStats, AssetStatsInline } from "../components/AssetStatsLegacy";
import {
  PositionRow,
  ClearPositionDialog,
  UpdateBudgetDialog,
  PauseConfirmDialog,
  SkipConfirmDialog,
  UpdateProfitTiersDialog,
  UpdateHedgeConfigDialog,
  PositionOpportunitiesTable,
  CompletedStrategiesTable,
  BulkActionToolbar,
  RegisterStrategyDialog,
  traderTheme as theme,
} from "../components/TraderDashboardLegacy";
import type {
  StrategyAction,
  BulkStrategyDisplayInfo,
} from "../components/TraderDashboardLegacy";
import { StrategyDetailDialog } from "../components/TraderDashboardLegacy/StrategyDetailDialog";
import HistoryIcon from "@mui/icons-material/History";
import { StandbyHedgedConfirmDialog } from "../components/OpportunityTable/StandbyHedgedConfirmDialog";
import {
  executeStrategyWithPayload,
  registerStrategy,
} from "../api/strategies";
import { Exchange } from "../types/futuresSpot";
import { preloadCcxt } from "../hooks/usePolledOrderbook";
import type { StrategyExecutionRequest } from "../api/strategies";
import { getExchangeLogo } from "../utils/exchangeUrls";
import { buildTraderOpportunitiesPath } from "../utils/traderRouting";
import { useAppSelector } from "../app/store";
import { applyStrategyStartTabPreference } from "../utils/strategyStartNavigation";
import { buildV1BuyOpenSpendInputs } from "../utils/assetStatsBudget";

/**
 * Sort configuration for positions
 */
type PositionSortField =
  | "symbol"
  | "state"
  | "duration"
  | "fundingRate"
  | "profit"
  | "startedAt"
  | "filled"
  | "liqPrice"
  | "margin"
  | "traded";
type SortDirection = "asc" | "desc";

interface PositionSortConfig {
  field: PositionSortField;
  direction: SortDirection;
}

const STATE_RANK: Record<string, number> = {
  BuyOpen: 1,
  PurchaseCrypto: 2,
  HedgeSetup: 3,
  Transfer: 4,
  NoTransfer: 5,
  Standby: 6,
  EvaluatePnL: 7,
  SellCrypto: 8,
  SellClose: 9,
  HedgeClose: 10,
  Success: 11,
  Failure: 12,
  Abort: 13,
};

/**
 * Sortable Header Cell Component
 */
interface SortableHeaderCellProps {
  field: PositionSortField;
  currentSort: PositionSortConfig;
  onSort: (field: PositionSortField) => void;
  children: React.ReactNode;
  width: string;
  textAlign?: "left" | "right" | "center";
  subText?: string;
  totalValue?: React.ReactNode;
  pr?: number;
}

const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  field,
  currentSort,
  onSort,
  children,
  width,
  textAlign = "left",
  subText,
  totalValue,
  pr: paddingRight,
}) => {
  const isActive = currentSort.field === field;

  return (
    <Box
      onClick={() => onSort(field)}
      sx={{
        width,
        fontSize: { xs: "0.7rem", md: "0.8rem" },
        fontWeight: 600,
        textAlign,
        cursor: "pointer",
        userSelect: "none",
        pr:
          paddingRight !== undefined
            ? paddingRight
            : textAlign === "right"
              ? 1
              : 0,
        "&:hover": {
          "& .MuiTypography-root": {
            color: theme.accent,
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent:
            textAlign === "right"
              ? "flex-end"
              : textAlign === "center"
                ? "center"
                : "flex-start",
          gap: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: "inherit",
            color: isActive ? theme.accent : theme.text.primary,
            fontWeight: isActive ? 700 : 600,
          }}
        >
          {children}
        </Typography>
        {isActive &&
          (currentSort.direction === "asc" ? (
            <ArrowUpwardIcon sx={{ fontSize: 12, color: theme.accent }} />
          ) : (
            <ArrowDownwardIcon sx={{ fontSize: 12, color: theme.accent }} />
          ))}
      </Box>
      {subText && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: theme.text.secondary,
            fontSize: { xs: "0.6rem", md: "0.7rem" },
          }}
        >
          {subText}
          {(field === "symbol" || field === "state") && isActive && (
            <Box
              component="span"
              sx={{
                ml: 0.5,
                fontSize: "0.6rem",
                color: theme.accent,
                opacity: 0.7,
              }}
            >
              ({field === "symbol" ? "Coin" : "State"})
            </Box>
          )}
        </Typography>
      )}
      {totalValue}
    </Box>
  );
};

/**
 * Position List Header Component
 */
interface PositionListHeaderProps {
  totalFundingRate: number;
  sortConfig: PositionSortConfig;
  onSort: (field: PositionSortField) => void;
  t: (key: string) => string;
}

const PositionListHeader: React.FC<PositionListHeaderProps> = ({
  totalFundingRate,
  sortConfig,
  onSort,
  t,
}) => (
  <Box
    sx={{
      display: "flex",
      backgroundColor: theme.bg.primary,
      borderBottom: `2px solid ${theme.border}`,
      py: 1,
      px: 1,
    }}
  >
    {/* Coin / Status Header - Separated Sorting */}
    <Box
      sx={{
        width: "12%",
        fontSize: { xs: "0.7rem", md: "0.8rem" },
        fontWeight: 600,
        textAlign: "center",
        userSelect: "none",
      }}
    >
      {/* Coin Sort */}
      <Box
        onClick={() => onSort("symbol")}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          cursor: "pointer",
          "&:hover": { "& .MuiTypography-root": { color: theme.accent } },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: "inherit",
            color:
              sortConfig.field === "symbol" ? theme.accent : theme.text.primary,
            fontWeight: sortConfig.field === "symbol" ? 700 : 600,
          }}
        >
          {t("traderDashboard.header.coin")}
        </Typography>
        {sortConfig.field === "symbol" &&
          (sortConfig.direction === "asc" ? (
            <ArrowUpwardIcon sx={{ fontSize: 12, color: theme.accent }} />
          ) : (
            <ArrowDownwardIcon sx={{ fontSize: 12, color: theme.accent }} />
          ))}
      </Box>

      {/* State Sort / Duration Sort - inline on one line */}
      <Typography
        variant="caption"
        sx={{ fontSize: { xs: "0.6rem", md: "0.7rem" }, textAlign: "center" }}
      >
        <Box
          component="span"
          onClick={() => onSort("state")}
          sx={{
            cursor: "pointer",
            color:
              sortConfig.field === "state"
                ? theme.accent
                : theme.text.secondary,
            fontWeight: sortConfig.field === "state" ? 700 : 400,
            "&:hover": { color: theme.accent },
          }}
        >
          {t("traderDashboard.header.status")}
          {sortConfig.field === "state" &&
            (sortConfig.direction === "asc" ? " ↑" : " ↓")}
        </Box>
        <Box component="span" sx={{ color: theme.text.secondary, mx: 0.25 }}>
          /
        </Box>
        <Box
          component="span"
          onClick={() => onSort("duration")}
          sx={{
            cursor: "pointer",
            color:
              sortConfig.field === "duration"
                ? theme.accent
                : theme.text.secondary,
            fontWeight: sortConfig.field === "duration" ? 700 : 400,
            "&:hover": { color: theme.accent },
          }}
        >
          경과
          {sortConfig.field === "duration" &&
            (sortConfig.direction === "asc" ? " ↑" : " ↓")}
        </Box>
      </Typography>
    </Box>
    <Box
      sx={{
        width: "12%",
        fontSize: { xs: "0.7rem", md: "0.8rem" },
        fontWeight: 600,
        textAlign: "left",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: theme.text.primary,
          fontSize: "inherit",
        }}
      >
        {t("traderDashboard.header.bep")}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: theme.text.secondary,
          fontSize: { xs: "0.6rem", md: "0.7rem" },
        }}
      >
        {t("traderDashboard.header.spotFutures")}
      </Typography>
    </Box>
    <Box
      sx={{
        width: "12%",
        fontSize: { xs: "0.7rem", md: "0.8rem" },
        fontWeight: 600,
        textAlign: "left",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: theme.text.primary,
          fontSize: "inherit",
        }}
      >
        {t("traderDashboard.header.currentPrice")}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: theme.text.secondary,
          fontSize: { xs: "0.6rem", md: "0.7rem" },
        }}
      >
        {t("traderDashboard.header.spotFutures")}
      </Typography>
    </Box>
    <Box
      sx={{
        width: "10%",
        fontSize: { xs: "0.7rem", md: "0.8rem" },
        fontWeight: 600,
        textAlign: "center",
      }}
    >
      {/* Liq Price Sort */}
      <Box
        onClick={() => onSort("liqPrice")}
        sx={{
          cursor: "pointer",
          "&:hover": { "& .MuiTypography-root": { color: theme.accent } },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color:
              sortConfig.field === "liqPrice" ? theme.accent : theme.danger,
            fontWeight: sortConfig.field === "liqPrice" ? 700 : 600,
            fontSize: "inherit",
          }}
        >
          {t("traderDashboard.header.liquidationPrice")}
          {sortConfig.field === "liqPrice" && (
            <Box component="span" sx={{ ml: 0.5 }}>
              {sortConfig.direction === "asc" ? "↑" : "↓"}
            </Box>
          )}
        </Typography>
      </Box>
      {/* Margin Sort */}
      <Box
        onClick={() => onSort("margin")}
        sx={{
          cursor: "pointer",
          "&:hover": { "& .MuiTypography-root": { color: theme.accent } },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color:
              sortConfig.field === "margin"
                ? theme.accent
                : theme.text.secondary,
            fontWeight: sortConfig.field === "margin" ? 700 : 400,
            fontSize: { xs: "0.6rem", md: "0.7rem" },
          }}
        >
          {t("traderDashboard.header.margin")}
          {sortConfig.field === "margin" && (
            <Box component="span" sx={{ ml: 0.5 }}>
              {sortConfig.direction === "asc" ? "↑" : "↓"}
            </Box>
          )}
        </Typography>
      </Box>
    </Box>
    <SortableHeaderCell
      field="fundingRate"
      currentSort={sortConfig}
      onSort={onSort}
      width="12%"
      textAlign="center"
      totalValue={
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontSize: { xs: "0.7rem", md: "0.8rem" },
            fontFamily: "monospace",
            color: totalFundingRate >= 0 ? theme.profit : theme.loss,
          }}
        >
          {totalFundingRate >= 0 ? "+" : ""}
          {totalFundingRate.toFixed(4)}%
        </Typography>
      }
    >
      {t("traderDashboard.header.fundingRate")}
    </SortableHeaderCell>
    <SortableHeaderCell
      field="filled"
      currentSort={sortConfig}
      onSort={onSort}
      width="6%"
      textAlign="center"
      subText={t("traderDashboard.header.spotFutures")}
    >
      {t("traderDashboard.header.balance")}
    </SortableHeaderCell>
    <SortableHeaderCell
      field="traded"
      currentSort={sortConfig}
      onSort={onSort}
      width="6%"
      textAlign="center"
      subText={t("traderDashboard.header.spotFutures")}
    >
      {t("traderDashboard.header.traded")}
    </SortableHeaderCell>
    <SortableHeaderCell
      field="profit"
      currentSort={sortConfig}
      onSort={onSort}
      width="20%"
      textAlign="right"
      subText={t("traderDashboard.header.spotFutures")}
      pr={4}
    >
      {t("traderDashboard.header.netPnl")}
    </SortableHeaderCell>
  </Box>
);

/**
 * Selected opportunity data for strategy execution dialog
 * Now simplified - live orderbook subscriptions are handled in the dialog
 */
interface SelectedOpportunity {
  baseAsset: string;
  spotExchange: Exchange;
  spotExchangeIndex: number;
  spotQuote: "USDT" | "KRW" | "BTC";
}

/**
 * Get exchange index from Exchange enum for backend
 */
function getExchangeIndexForBackend(exchange: Exchange): number {
  switch (exchange) {
    case Exchange.Upbit:
      return 0;
    case Exchange.Bithumb:
      return 1;
    case Exchange.Binance:
      return 3;
    default:
      return 3;
  }
}

/**
 * Tab panel component
 */
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      style={{
        display: value === index ? "flex" : "none",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {value === index && (
        <Box
          sx={{
            pt: 2,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
};

/**
 * Props for DashboardContent
 */
interface DashboardContentProps {
  activeTab: number;
  setActiveTab: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * Main Dashboard Content Component
 */
const DashboardContent: React.FC<DashboardContentProps> = ({
  activeTab,
  setActiveTab,
}) => {
  const settings = useAppSelector((state) => state.settings.settings);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    strategies,
    traderName,
    count,
    loading,
    error,
    refreshStrategies,
    emergencyClearStrategy,
    pauseMonitoring,
    reloadStrategy,
    skipStrategy,
    excludeStrategy,
    updateBudget,
    updateProfitTiers,
    updateHedgeConfig,
    completedCount,
    refreshCompletedStrategies,
    // Bulk operations
    bulkPause,
    bulkResume,
    bulkReload,
    bulkUpdateBudget,
    bulkUpdateProfitTiers,
    bulkUpdateHedgeConfig,
  } = useTraderData();
  const buyOpenSpendInputs = useMemo(
    () => buildV1BuyOpenSpendInputs(strategies),
    [strategies],
  );

  // Clear position dialog state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState<StrategyInfo | null>(
    null,
  );
  const [clearLoading, setClearLoading] = useState(false);

  // Update budget dialog state
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [budgetDialogStrategy, setBudgetDialogStrategy] =
    useState<StrategyInfo | null>(null);
  const [budgetLoading, setBudgetLoading] = useState(false);

  // Pause confirmation dialog state
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pauseDialogStrategy, setPauseDialogStrategy] =
    useState<StrategyInfo | null>(null);
  const [pauseLoading, setPauseLoading] = useState(false);

  // Skip confirmation dialog state
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipDialogStrategy, setSkipDialogStrategy] =
    useState<StrategyInfo | null>(null);
  const [skipLoading, setSkipLoading] = useState(false);

  // Update profit tiers dialog state
  const [profitTiersDialogOpen, setProfitTiersDialogOpen] = useState(false);
  const [profitTiersDialogStrategy, setProfitTiersDialogStrategy] =
    useState<StrategyInfo | null>(null);
  const [profitTiersLoading, setProfitTiersLoading] = useState(false);

  // Update hedge config dialog state
  const [hedgeConfigDialogOpen, setHedgeConfigDialogOpen] = useState(false);
  const [hedgeConfigDialogStrategy, setHedgeConfigDialogStrategy] =
    useState<StrategyInfo | null>(null);
  const [hedgeConfigLoading, setHedgeConfigLoading] = useState(false);

  // Per-strategy action loading state (for UI feedback)
  const [actionLoadingMap, setActionLoadingMap] = useState<
    Record<string, StrategyAction | null>
  >({});

  // Multi-select state for bulk operations
  const [selectedStrategyIds, setSelectedStrategyIds] = useState<Set<string>>(
    new Set(),
  );
  const [bulkBudgetDialogOpen, setBulkBudgetDialogOpen] = useState(false);
  const [bulkProfitTiersDialogOpen, setBulkProfitTiersDialogOpen] =
    useState(false);
  const [bulkHedgeConfigDialogOpen, setBulkHedgeConfigDialogOpen] =
    useState(false);

  // Strategy execution dialog state
  const [strategyDialogOpen, setStrategyDialogOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] =
    useState<SelectedOpportunity | null>(null);

  // Register strategy dialog state
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);

  // Strategy Detail Dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailStrategyId, setDetailStrategyId] = useState<string | null>(null);

  // Position sorting state - default to profit descending (most profitable first)
  const [sortConfig, setSortConfig] = useState<PositionSortConfig>({
    field: "profit",
    direction: "desc",
  });

  // Search filter for active positions
  const [searchQuery, setSearchQuery] = useState("");

  // Exchange filter state for active positions
  const [exchangeFilters, setExchangeFilters] = useState<
    Record<string, boolean>
  >({
    binance: true,
    upbit: true,
    bithumb: true,
  });

  // Hover lock state - freezes sort order when hovering
  const [hoveredStrategyId, setHoveredStrategyId] = useState<string | null>(
    null,
  );
  const [frozenOrder, setFrozenOrder] = useState<string[] | null>(null);

  // Header stats minimized state - persisted in localStorage
  const [isHeaderMinimized, setIsHeaderMinimized] = useState(() => {
    const saved = localStorage.getItem("headerStatsMinimized");
    return saved === "true";
  });

  // Persist minimize preference
  const handleHeaderMinimizeChange = useCallback((minimized: boolean) => {
    setIsHeaderMinimized(minimized);
    localStorage.setItem("headerStatsMinimized", String(minimized));
  }, []);

  // Multi-select handlers
  const handleSelectionChange = useCallback(
    (strategyId: string, selected: boolean) => {
      setSelectedStrategyIds((prev) => {
        const newSet = new Set(prev);
        if (selected) {
          newSet.add(strategyId);
        } else {
          newSet.delete(strategyId);
        }
        return newSet;
      });
    },
    [],
  );

  const handleClearSelection = useCallback(() => {
    setSelectedStrategyIds(new Set());
  }, []);

  // Build selected strategies info for bulk dialogs
  const selectedStrategiesInfo = useMemo((): BulkStrategyDisplayInfo[] => {
    return strategies
      .filter((s) => selectedStrategyIds.has(s.strategy_id))
      .map((s) => {
        const ctx = s.execution_context;
        const symbol =
          ctx.opportunity?.base ||
          ctx.position?.symbol?.replace("USDT", "") ||
          "-";
        const orders = Object.values(ctx.orders || {});
        const exchangeName =
          orders.length > 0
            ? (orders[0].exchange || "binance").toLowerCase()
            : "binance";
        // Get budget from opportunity.max_profitable_volume (USDT budget)
        const budget = ctx.opportunity?.max_profitable_volume;
        // Get profit tiers from execution context
        // Get profit tiers from execution context
        const profitTiers = ctx.profit_tiers || undefined;
        // Get entry diff (converted directly to percentage for display if needed, but here we pass raw ratio if stored that way)
        // Note: In UpdateBudgetDialog logic, quote_conversion_rate is treated as the ratio (e.g. -0.005) or percentage depending on storage.
        // Looking at UpdateBudgetDialog line 133: setEntryDiffRatio(currentRatio * 100).
        // So quote_conversion_rate is likely the decimal value (e.g. -0.005).
        // Let's pass it as is and let the component handle display, OR convert to percentage here.
        // The dialog expects raw value to verify against, but for 'entryDiff' display we usually want percentage.
        // Let's look at how UpdateBudgetDialog uses it. It's just for display.
        // Existing code: setEntryDiffRatio(currentRatio * 100). So it is decimal.
        // We probably want to display percentage.
        const entryDiff = ctx.opportunity?.quote_conversion_rate
          ? ctx.opportunity.quote_conversion_rate * 100
          : undefined;
        // Get hedge config from execution context
        const hedgeConfig = ctx.hedge_config;

        return {
          strategyId: s.strategy_id,
          symbol,
          exchangeName,
          budget,
          profitTiers,
          entryDiff,
          hedgeConfig,
        };
      });
  }, [strategies, selectedStrategyIds]);

  // Handle exchange filter toggle
  const handleExchangeToggle = useCallback((exchange: string) => {
    setExchangeFilters((prev) => ({
      ...prev,
      [exchange]: !prev[exchange],
    }));
  }, []);

  // Handle sort change
  const handleSort = useCallback((field: PositionSortField) => {
    setSortConfig((prev) => {
      let direction: SortDirection = "asc";
      if (prev.field === field) {
        direction = prev.direction === "asc" ? "desc" : "asc";
      } else {
        // Default direction for new field
        if (field === "symbol" || field === "state" || field === "duration") {
          direction = "asc";
        } else {
          direction = "desc";
        }
      }
      const newConfig = { field, direction };

      // Persist to localStorage
      try {
        localStorage.setItem(
          "trader_dashboard_sort",
          JSON.stringify(newConfig),
        );
      } catch (e) {
        console.error("Failed to save sort config", e);
      }

      return newConfig;
    });
    // Clear frozen order when user changes sort
    setFrozenOrder(null);
    setHoveredStrategyId(null);
  }, []);

  // Handle row hover start - freeze the current sort order
  const handleRowHoverStart = useCallback(
    (strategyId: string, currentOrder: string[]) => {
      setHoveredStrategyId(strategyId);
      setFrozenOrder(currentOrder);
    },
    [],
  );

  // Handle row hover end - unfreeze sort order
  const handleRowHoverEnd = useCallback(() => {
    setHoveredStrategyId(null);
    setFrozenOrder(null);
  }, []);

  /**
   * Unified handler for all strategy actions
   */
  const handleStrategyAction = useCallback(
    async (strategyId: string, action: StrategyAction) => {
      const strategy = strategies.find((s) => s.strategy_id === strategyId);
      if (!strategy) return;

      // Handle actions that open dialogs
      if (action === "view_details") {
        setDetailStrategyId(strategyId);
        setDetailDialogOpen(true);
        return;
      }

      if (action === "emergency_clear") {
        setSelectedStrategy(strategy);
        setClearDialogOpen(true);
        return;
      }

      if (action === "update_budget") {
        setBudgetDialogStrategy(strategy);
        setBudgetDialogOpen(true);
        return;
      }

      // Pause requires confirmation dialog
      if (action === "pause") {
        setPauseDialogStrategy(strategy);
        setPauseDialogOpen(true);
        return;
      }

      // Skip requires confirmation dialog
      if (action === "skip") {
        setSkipDialogStrategy(strategy);
        setSkipDialogOpen(true);
        return;
      }

      // Update profit tiers requires dialog
      if (action === "update_profit_tiers") {
        setProfitTiersDialogStrategy(strategy);
        setProfitTiersDialogOpen(true);
        return;
      }

      // Update hedge config requires dialog
      if (action === "update_hedge_config") {
        setHedgeConfigDialogStrategy(strategy);
        setHedgeConfigDialogOpen(true);
        return;
      }

      // Handle direct actions (resume, reload, exclude)
      setActionLoadingMap((prev) => ({ ...prev, [strategyId]: action }));

      try {
        let success = false;
        if (action === "resume") {
          success = await pauseMonitoring(strategyId);
        } else if (action === "reload") {
          success = await reloadStrategy(strategyId);
        } else if (action === "exclude") {
          success = await excludeStrategy(strategyId);
        }

        if (!success) {
          console.error(`Failed to execute action: ${action}`);
        }
      } catch (err) {
        console.error(`Error executing action ${action}:`, err);
      } finally {
        setActionLoadingMap((prev) => ({ ...prev, [strategyId]: null }));
      }
    },
    [strategies, pauseMonitoring, reloadStrategy, excludeStrategy],
  );

  const handleConfirmClear = useCallback(async () => {
    if (!selectedStrategy) return;

    setClearLoading(true);
    try {
      const success = await emergencyClearStrategy(
        selectedStrategy.strategy_id,
      );
      if (success) {
        console.log("Emergency clear triggered successfully");
      } else {
        console.error("Failed to trigger emergency clear");
      }
    } catch (err) {
      console.error("Error triggering emergency clear:", err);
    } finally {
      setClearLoading(false);
      setClearDialogOpen(false);
      setSelectedStrategy(null);
    }
  }, [selectedStrategy, emergencyClearStrategy]);

  const handleCloseDialog = useCallback(() => {
    setClearDialogOpen(false);
    setSelectedStrategy(null);
  }, []);

  /**
   * Handle increase budget dialog confirm
   */
  const handleConfirmBudget = useCallback(
    async (totalBudgetUsdt: number, entryDifferenceRatio?: number) => {
      if (!budgetDialogStrategy) return;

      setBudgetLoading(true);
      try {
        const success = await updateBudget(budgetDialogStrategy.strategy_id, {
          totalBudgetUsdt,
          entryDifferenceRatio,
        });
        if (success) {
          console.log("Budget updated successfully");
        } else {
          console.error("Failed to update budget");
        }
      } catch (err) {
        console.error("Error updating budget:", err);
      } finally {
        setBudgetLoading(false);
        setBudgetDialogOpen(false);
        setBudgetDialogStrategy(null);
      }
    },
    [budgetDialogStrategy, updateBudget],
  );

  /**
   * Handle update budget dialog close
   */
  const handleCloseBudgetDialog = useCallback(() => {
    setBudgetDialogOpen(false);
    setBudgetDialogStrategy(null);
  }, []);

  /**
   * Handle pause confirmation
   */
  const handleConfirmPause = useCallback(async () => {
    if (!pauseDialogStrategy) return;

    setPauseLoading(true);
    try {
      const success = await pauseMonitoring(pauseDialogStrategy.strategy_id);
      if (success) {
        console.log("Strategy paused successfully");
      } else {
        console.error("Failed to pause strategy");
      }
    } catch (err) {
      console.error("Error pausing strategy:", err);
    } finally {
      setPauseLoading(false);
      setPauseDialogOpen(false);
      setPauseDialogStrategy(null);
    }
  }, [pauseDialogStrategy, pauseMonitoring]);

  /**
   * Handle pause dialog close
   */
  const handleClosePauseDialog = useCallback(() => {
    setPauseDialogOpen(false);
    setPauseDialogStrategy(null);
  }, []);

  /**
   * Handle skip confirmation
   */
  const handleConfirmSkip = useCallback(async () => {
    if (!skipDialogStrategy) return;

    setSkipLoading(true);
    try {
      const success = await skipStrategy(skipDialogStrategy.strategy_id);
      if (success) {
        console.log("Strategy skip triggered successfully");
      } else {
        console.error("Failed to skip strategy");
      }
    } catch (err) {
      console.error("Error skipping strategy:", err);
    } finally {
      setSkipLoading(false);
      setSkipDialogOpen(false);
      setSkipDialogStrategy(null);
    }
  }, [skipDialogStrategy, skipStrategy]);

  /**
   * Handle skip dialog close
   */
  const handleCloseSkipDialog = useCallback(() => {
    setSkipDialogOpen(false);
    setSkipDialogStrategy(null);
  }, []);

  /**
   * Handle update profit tiers confirmation
   */
  const handleConfirmProfitTiers = useCallback(
    async (profitTiers: ProfitTier[]) => {
      if (!profitTiersDialogStrategy) return;

      setProfitTiersLoading(true);
      try {
        const success = await updateProfitTiers(
          profitTiersDialogStrategy.strategy_id,
          profitTiers,
        );
        if (success) {
          console.log("Profit tiers updated successfully");
        } else {
          console.error("Failed to update profit tiers");
        }
      } catch (err) {
        console.error("Error updating profit tiers:", err);
      } finally {
        setProfitTiersLoading(false);
        setProfitTiersDialogOpen(false);
        setProfitTiersDialogStrategy(null);
      }
    },
    [profitTiersDialogStrategy, updateProfitTiers],
  );

  /**
   * Handle profit tiers dialog close
   */
  const handleCloseProfitTiersDialog = useCallback(() => {
    setProfitTiersDialogOpen(false);
    setProfitTiersDialogStrategy(null);
  }, []);

  /**
   * Handle update hedge config confirmation
   */
  const handleConfirmHedgeConfig = useCallback(
    async (
      leverage?: number,
      rebalancing?: {
        price_surge_threshold?: number;
        liquidation_proximity_threshold?: number;
        entry_funding_rate_threshold?: number | null;
        entry_funding_interval_hours?: number;
        spot_futures_gap_magnitude_ratio?: number;
        low_funding_rate_threshold?: number;
        funding_time_window_seconds?: number;
      },
    ) => {
      if (!hedgeConfigDialogStrategy) return;

      setHedgeConfigLoading(true);
      try {
        const success = await updateHedgeConfig(
          hedgeConfigDialogStrategy.strategy_id,
          leverage,
          rebalancing,
        );
        if (success) {
          console.log("Hedge config updated successfully");
        } else {
          console.error("Failed to update hedge config");
        }
      } catch (err) {
        console.error("Error updating hedge config:", err);
      } finally {
        setHedgeConfigLoading(false);
        setHedgeConfigDialogOpen(false);
        setHedgeConfigDialogStrategy(null);
      }
    },
    [hedgeConfigDialogStrategy, updateHedgeConfig],
  );

  /**
   * Handle hedge config dialog close
   */
  const handleCloseHedgeConfigDialog = useCallback(() => {
    setHedgeConfigDialogOpen(false);
    setHedgeConfigDialogStrategy(null);
  }, []);

  /**
   * Handle bulk budget dialog confirm
   */
  const [bulkBudgetLoading, setBulkBudgetLoading] = useState(false);
  const handleConfirmBulkBudget = useCallback(
    async (totalBudgetUsdt: number, entryDifferenceRatio?: number) => {
      if (selectedStrategyIds.size === 0) return;

      setBulkBudgetLoading(true);
      try {
        await bulkUpdateBudget(
          Array.from(selectedStrategyIds),
          totalBudgetUsdt,
          entryDifferenceRatio,
        );
        handleClearSelection();
      } catch (err) {
        console.error("Error bulk updating budget:", err);
      } finally {
        setBulkBudgetLoading(false);
        setBulkBudgetDialogOpen(false);
      }
    },
    [selectedStrategyIds, bulkUpdateBudget, handleClearSelection],
  );

  /**
   * Handle bulk budget dialog close
   */
  const handleCloseBulkBudgetDialog = useCallback(() => {
    setBulkBudgetDialogOpen(false);
  }, []);

  /**
   * Handle bulk profit tiers dialog confirm
   */
  const [bulkProfitTiersLoading, setBulkProfitTiersLoading] = useState(false);
  const handleConfirmBulkProfitTiers = useCallback(
    async (profitTiers: ProfitTier[]) => {
      if (selectedStrategyIds.size === 0) return;

      setBulkProfitTiersLoading(true);
      try {
        await bulkUpdateProfitTiers(
          Array.from(selectedStrategyIds),
          profitTiers,
        );
        handleClearSelection();
      } catch (err) {
        console.error("Error bulk updating profit tiers:", err);
      } finally {
        setBulkProfitTiersLoading(false);
        setBulkProfitTiersDialogOpen(false);
      }
    },
    [selectedStrategyIds, bulkUpdateProfitTiers, handleClearSelection],
  );

  /**
   * Handle bulk profit tiers dialog close
   */
  const handleCloseBulkProfitTiersDialog = useCallback(() => {
    setBulkProfitTiersDialogOpen(false);
  }, []);

  /**
   * Handle opening budget dialog from bulk toolbar
   * If only 1 strategy selected, open single strategy dialog instead
   */
  const handleOpenBudgetDialogFromToolbar = useCallback(() => {
    if (selectedStrategyIds.size === 1) {
      // Single strategy - open single dialog
      const strategyId = Array.from(selectedStrategyIds)[0];
      const strategy = strategies.find((s) => s.strategy_id === strategyId);
      if (strategy) {
        setBudgetDialogStrategy(strategy);
        setBudgetDialogOpen(true);
      }
    } else {
      // Multiple strategies - open bulk dialog
      setBulkBudgetDialogOpen(true);
    }
  }, [selectedStrategyIds, strategies]);

  /**
   * Handle opening profit tiers dialog from bulk toolbar
   * If only 1 strategy selected, open single strategy dialog instead
   */
  const handleOpenProfitTiersDialogFromToolbar = useCallback(() => {
    if (selectedStrategyIds.size === 1) {
      // Single strategy - open single dialog
      const strategyId = Array.from(selectedStrategyIds)[0];
      const strategy = strategies.find((s) => s.strategy_id === strategyId);
      if (strategy) {
        setProfitTiersDialogStrategy(strategy);
        setProfitTiersDialogOpen(true);
      }
    } else {
      // Multiple strategies - open bulk dialog
      setBulkProfitTiersDialogOpen(true);
    }
  }, [selectedStrategyIds, strategies]);

  /**
   * Handle bulk hedge config dialog confirm
   */
  const [bulkHedgeConfigLoading, setBulkHedgeConfigLoading] = useState(false);
  const handleConfirmBulkHedgeConfig = useCallback(
    async (
      leverage?: number,
      rebalancing?: {
        price_surge_threshold?: number;
        liquidation_proximity_threshold?: number;
        entry_funding_rate_threshold?: number | null;
        entry_funding_interval_hours?: number;
        spot_futures_gap_magnitude_ratio?: number;
        low_funding_rate_threshold?: number;
        funding_time_window_seconds?: number;
      },
    ) => {
      if (selectedStrategyIds.size === 0) return;

      setBulkHedgeConfigLoading(true);
      try {
        await bulkUpdateHedgeConfig(
          Array.from(selectedStrategyIds),
          leverage,
          rebalancing,
        );
        handleClearSelection();
      } catch (err) {
        console.error("Error bulk updating hedge config:", err);
      } finally {
        setBulkHedgeConfigLoading(false);
        setBulkHedgeConfigDialogOpen(false);
      }
    },
    [selectedStrategyIds, bulkUpdateHedgeConfig, handleClearSelection],
  );

  /**
   * Handle bulk hedge config dialog close
   */
  const handleCloseBulkHedgeConfigDialog = useCallback(() => {
    setBulkHedgeConfigDialogOpen(false);
  }, []);

  /**
   * Handle opening hedge config dialog from bulk toolbar
   * If only 1 strategy selected, open single strategy dialog instead
   */
  const handleOpenHedgeConfigDialogFromToolbar = useCallback(() => {
    if (selectedStrategyIds.size === 1) {
      // Single strategy - open single dialog
      const strategyId = Array.from(selectedStrategyIds)[0];
      const strategy = strategies.find((s) => s.strategy_id === strategyId);
      if (strategy) {
        setHedgeConfigDialogStrategy(strategy);
        setHedgeConfigDialogOpen(true);
      }
    } else {
      // Multiple strategies - open bulk dialog
      setBulkHedgeConfigDialogOpen(true);
    }
  }, [selectedStrategyIds, strategies]);

  // Tab change handler
  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
      // Preload CCXT when switching to New Position tab (tab 1)
      // so the dialog opens faster
      if (newValue === 1) {
        preloadCcxt();
      }
      // Refresh completed strategies when switching to History tab (tab 2)
      if (newValue === 2) {
        refreshCompletedStrategies();
      }
    },
    [setActiveTab, refreshCompletedStrategies],
  );

  // Launch strategy from opportunity - opens dialog with live orderbook subscriptions
  const handleLaunchStrategy = useCallback(
    (opportunity: PositionOpportunity, spotIndex: number) => {
      // Get the specific spot exchange data that user clicked on
      const spotData = opportunity.spotData[spotIndex];

      if (!spotData) {
        console.error("No spot data found for index:", spotIndex);
        return;
      }

      // Set simplified opportunity - dialog handles live orderbook subscriptions
      setSelectedOpportunity({
        baseAsset: opportunity.baseAsset,
        spotExchange: spotData.exchange,
        spotExchangeIndex: getExchangeIndexForBackend(spotData.exchange),
        spotQuote: spotData.quote,
      });
      setStrategyDialogOpen(true);
    },
    [],
  );

  // Cancel strategy dialog - just close without switching tabs
  const handleCancelStrategyDialog = useCallback(() => {
    setStrategyDialogOpen(false);
    setSelectedOpportunity(null);
  }, []);

  // Execute strategy and apply the saved post-start tab preference on success
  const handleExecuteStrategy = useCallback(
    async (payload: StrategyExecutionRequest) => {
      if (!traderName) {
        console.error("No trader name available");
        return;
      }
      await executeStrategyWithPayload(payload, traderName);
      setStrategyDialogOpen(false);
      setSelectedOpportunity(null);
      // Refresh strategies to show the new one
      refreshStrategies();
      applyStrategyStartTabPreference(
        settings.display.switchToActivePositionsAfterStart,
        setActiveTab,
      );
    },
    [
      refreshStrategies,
      setActiveTab,
      settings.display.switchToActivePositionsAfterStart,
      traderName,
    ],
  );

  // Open register strategy dialog
  const handleOpenRegisterDialog = useCallback(() => {
    setRegisterError(null);
    setRegisterDialogOpen(true);
  }, []);

  // Close register strategy dialog
  const handleCloseRegisterDialog = useCallback(() => {
    setRegisterDialogOpen(false);
    setRegisterError(null);
  }, []);

  // Handle detail dialog close
  const handleCloseDetailDialog = useCallback(() => {
    setDetailDialogOpen(false);
    setDetailStrategyId(null);
  }, []);

  // Handle register strategy confirmation
  const handleRegisterStrategy = useCallback(
    async (strategyId: string) => {
      if (!traderName) {
        setRegisterError("Trader name is required");
        return;
      }

      setRegisterLoading(true);
      setRegisterError(null);

      try {
        const result = await registerStrategy(traderName, strategyId);
        console.log("Strategy registered:", result);

        // Close dialog on success
        setRegisterDialogOpen(false);

        // Refresh strategies list
        refreshStrategies();

        // If strategy was added to completed, refresh completed list and switch to history tab
        if (result.action === "AddedToCompleted") {
          refreshCompletedStrategies();
          setActiveTab(2); // History tab
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to register strategy";
        setRegisterError(message);
      } finally {
        setRegisterLoading(false);
      }
    },
    [traderName, refreshStrategies, refreshCompletedStrategies, setActiveTab],
  );

  // Calculate total funding rate for header
  const totalFundingRate = strategies.reduce((sum, s) => {
    const rate = s.realtime_prices?.funding_rate;
    return sum + (rate != null ? rate * 100 : 0);
  }, 0);

  // Extract base assets from active strategies for disabling already-opened positions
  const activePositionBases = useMemo(() => {
    const bases = new Set<string>();
    strategies.forEach((s) => {
      // Get base asset from execution_context.opportunity.base
      const base = s.execution_context?.opportunity?.base;
      if (base) {
        bases.add(base);
      }
    });
    return bases;
  }, [strategies]);

  // Helper function to get symbol from strategy
  const getSymbol = (s: StrategyInfo): string => {
    return (
      s.execution_context?.opportunity?.base ||
      s.execution_context?.position?.symbol?.replace("USDT", "") ||
      ""
    );
  };

  // Helper function to get exchange name from strategy
  const getExchangeName = (s: StrategyInfo): string => {
    const ctx = s.execution_context;
    if (!ctx) return "binance";
    const orders = Object.values(ctx.orders || {});
    if (orders.length > 0) {
      return (orders[0].exchange || "binance").toLowerCase();
    }
    return "binance";
  };

  // Helper function to estimate net profit (simplified calculation)
  const getNetProfit = (s: StrategyInfo): number => {
    const ctx = s.execution_context;
    if (!ctx) return 0;

    // Get spot orders and their costs
    const spotBuyOrders = Object.values(ctx.orders || {}).filter(
      (o) => o.side === "Bid",
    );

    // Calculate spot cost by converting each order accurately
    const spotCost = spotBuyOrders.reduce((sum, o) => {
      const rate = o.quote_to_usdt_rate ?? 1;
      return sum + (o.total ?? 0) * rate;
    }, 0);

    // Calculate total executed volume for current value
    const spotVolume = spotBuyOrders.reduce(
      (sum, o) => sum + o.executed_volume,
      0,
    );

    // Get futures entry and calculate PnL
    const position = ctx.position;
    const futuresPrice =
      s.realtime_prices?.futures_mark_price ?? position?.mark_price ?? 0;
    const spotPrice = s.realtime_prices?.spot_best_bid ?? 0;

    // Estimate current value of spot holdings (convert to USDT)
    // Use the latest available quote rate from orders as proxy for current rate if needed
    // or just the last used rate.
    const lastOrder = spotBuyOrders[spotBuyOrders.length - 1];
    const estimatedQuoteToUsdtRate = lastOrder?.quote_to_usdt_rate ?? 1;

    const spotValue = spotVolume * spotPrice * estimatedQuoteToUsdtRate;
    // Futures PnL (short position)
    const futuresPnl = position
      ? (position.entry_price - futuresPrice) *
        Math.abs(position.position_amount)
      : 0;

    const totalFundingFees =
      s.execution_context.metrics?.total_funding_fees ?? 0;
    return spotValue - spotCost + futuresPnl + totalFundingFees;
  };

  // Helper function to calculate traded notional value for 체결 column
  // 체결 = notional value of spot asked volume + futures closed volume (in USDT)
  const getTradedVolume = (s: StrategyInfo): number => {
    const ctx = s.execution_context;
    if (!ctx) return 0;

    // Spot Asked: Calculate notional value of Ask orders (selling spot)
    const spotAskOrders = Object.values(ctx.orders || {}).filter(
      (o) => o.side === "Ask",
    );
    const spotAskedNotional = spotAskOrders.reduce((sum, o) => {
      const volume = o.executed_volume || 0;
      const price = o.price || 0;
      const total = o.total || volume * price;
      const rate = o.quote_to_usdt_rate ?? 1;
      return sum + total * rate;
    }, 0);

    // Futures Closed: Calculate notional value of Bid orders (closing short position)
    const futuresBidOrders = Object.values(ctx.futures_orders || {}).filter(
      (o) => o.side === "Bid",
    );
    const futuresClosedNotional = futuresBidOrders.reduce((sum, o) => {
      const volume = o.executed_volume || 0;
      const price = o.avg_price || o.price || 0;
      return sum + volume * price;
    }, 0);

    // Total traded notional = spot asked + futures closed (both in USDT)
    return spotAskedNotional + futuresClosedNotional;
  };

  // Helper function to calculate liquidation margin percentage
  // margin percentage = (current_price / liquidation_price) * 100
  // For example: if current price is $10 and liquidation is $50, margin = 20%
  // Uses the same price displayed in Current Price column (futures_best_ask || futures_mark_price)
  const calculateLiquidationMarginPercentage = (s: StrategyInfo): number => {
    const position = s.execution_context?.position;
    if (!position) return 0;

    // Use the same price as displayed in Current Price column (matching pnl.futures.currentPrice)
    const currentPrice =
      s.realtime_prices?.futures_best_ask ||
      s.realtime_prices?.futures_mark_price ||
      position.mark_price;
    const liquidationPrice = position.liquidation_price;

    if (!currentPrice || !liquidationPrice || liquidationPrice === 0) return 0;

    // Margin percentage = (current_price / liquidation_price) * 100
    return (currentPrice / liquidationPrice) * 100;
  };

  // Helper to get state rank
  const getStateRank = useCallback((state: string): number => {
    return STATE_RANK[state] ?? 99;
  }, []);

  // Natural sorted strategies (without frozen order)
  const naturalSortedStrategies = useMemo(() => {
    if (!strategies.length) return strategies;

    // Filter by search query (symbol or strategy_id)
    let filtered = searchQuery.trim()
      ? strategies.filter((s) => {
          const query = searchQuery.toLowerCase().trim();
          const symbol = getSymbol(s).toLowerCase();
          const strategyId = s.strategy_id.toLowerCase();
          return symbol.includes(query) || strategyId.includes(query);
        })
      : strategies;

    // Filter by exchange
    filtered = filtered.filter((s) => {
      const exchange = getExchangeName(s);
      return exchangeFilters[exchange] !== false;
    });

    return [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortConfig.field) {
        case "symbol":
          comparison = getSymbol(a).localeCompare(getSymbol(b));
          break;
        case "state": {
          const rankA = getStateRank(a.current_state);
          const rankB = getStateRank(b.current_state);
          if (rankA !== rankB) {
            comparison = rankA - rankB;
          } else {
            // Secondary sort by symbol if states are same
            comparison = getSymbol(a).localeCompare(getSymbol(b));
          }
          break;
        }
        case "duration": {
          const detectedA =
            a.execution_context?.opportunity?.detected_at ?? a.started_at ?? 0;
          const detectedB =
            b.execution_context?.opportunity?.detected_at ?? b.started_at ?? 0;
          // Earlier detected_at = longer duration
          comparison = detectedA - detectedB;
          break;
        }
        case "fundingRate": {
          const rateA = a.realtime_prices?.funding_rate ?? 0;
          const rateB = b.realtime_prices?.funding_rate ?? 0;
          comparison = rateA - rateB;
          break;
        }
        case "profit":
          comparison = getNetProfit(a) - getNetProfit(b);
          break;
        case "filled":
          comparison = getTradedVolume(a) - getTradedVolume(b);
          break;
        case "traded":
          comparison = getTradedVolume(a) - getTradedVolume(b);
          break;
        case "liqPrice": {
          // Sort by actual liquidation price value
          const liqPriceA =
            a.execution_context?.position?.liquidation_price ?? 0;
          const liqPriceB =
            b.execution_context?.position?.liquidation_price ?? 0;
          comparison = liqPriceA - liqPriceB;
          break;
        }
        case "margin": {
          // Sort by liquidation margin percentage
          // margin percentage = (liquidation_distance / current_price) * 100
          // Higher percentage means more margin safety (further from liquidation)
          const marginPercentA = calculateLiquidationMarginPercentage(a);
          const marginPercentB = calculateLiquidationMarginPercentage(b);
          comparison = marginPercentA - marginPercentB;
          break;
        }
        case "startedAt":
          comparison = a.started_at - b.started_at;
          break;
      }

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [strategies, sortConfig, searchQuery, exchangeFilters, getStateRank]);

  // Current sort order as array of strategy IDs (for freezing)
  const currentSortOrder = useMemo(() => {
    return naturalSortedStrategies.map((s) => s.strategy_id);
  }, [naturalSortedStrategies]);

  // Final sorted strategies - uses frozen order when hovering
  const sortedStrategies = useMemo(() => {
    // If we have a frozen order and a hovered strategy, use frozen order
    if (frozenOrder && hoveredStrategyId) {
      // Create a map for quick lookup of current strategy data
      const strategyMap = new Map(strategies.map((s) => [s.strategy_id, s]));

      // Return strategies in frozen order, but with latest data
      return frozenOrder
        .map((id) => strategyMap.get(id))
        .filter((s): s is StrategyInfo => s !== undefined);
    }

    // Otherwise use natural sorted order
    return naturalSortedStrategies;
  }, [naturalSortedStrategies, frozenOrder, hoveredStrategyId, strategies]);

  // Select all handler - must be after sortedStrategies is defined
  const handleSelectAll = useCallback(() => {
    // Select only filtered/visible strategies, not all strategies
    setSelectedStrategyIds(new Set(sortedStrategies.map((s) => s.strategy_id)));
  }, [sortedStrategies]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.bg.primary,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Header with ARBITREASURE Title and Position Management */}
        <Box sx={{ pt: 2, pb: 2, px: { xs: 2, sm: 3, md: 4 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                onClick={() =>
                  navigate(buildTraderOpportunitiesPath(traderName, "v1"))
                }
                sx={{ color: theme.text.secondary }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1, sm: 2 },
                }}
              >
                {/* ARBITREASURE animated title */}
                <Typography
                  variant="h4"
                  component="span"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.5rem", md: "1.75rem" },
                    fontWeight: "bold",
                    letterSpacing: { xs: "0.02em", sm: "0.05em" },
                    textTransform: "uppercase",
                    background:
                      "linear-gradient(135deg, #00ffff 0%, #ff00ff 50%, #00ffff 100%)",
                    backgroundSize: "200% auto",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    animation: "gradient 3s ease 10",
                    "@keyframes gradient": {
                      "0%": { backgroundPosition: "0% 50%" },
                      "50%": { backgroundPosition: "100% 50%" },
                      "100%": { backgroundPosition: "0% 50%" },
                    },
                  }}
                >
                  {t("app.title").toUpperCase()}
                </Typography>
                {/* Compact tabs when minimized - icon only, right after title */}
                {isHeaderMinimized && (
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{
                      minHeight: "auto",
                      "& .MuiTab-root": {
                        minHeight: 36,
                        minWidth: 36,
                        padding: "6px",
                        color: theme.text.secondary,
                        "&.Mui-selected": {
                          color: theme.accent,
                        },
                      },
                      "& .MuiTabs-indicator": {
                        backgroundColor: theme.accent,
                      },
                    }}
                  >
                    <Tab
                      icon={<ListAltIcon fontSize="small" />}
                      aria-label="Active Positions"
                    />
                    <Tab
                      icon={<AddCircleOutlineIcon fontSize="small" />}
                      aria-label="New Position"
                    />
                    <Tab
                      icon={<HistoryIcon fontSize="small" />}
                      aria-label="History"
                    />
                  </Tabs>
                )}
                {/* Position Management title - hide when minimized */}
                {!isHeaderMinimized && (
                  <Typography
                    variant="h4"
                    component="span"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "1rem", sm: "1.5rem" },
                      color: theme.text.primary,
                    }}
                  >
                    {t("traderDashboard.title")}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Inline header stats when minimized */}
              {isHeaderMinimized && (
                <>
                  <HeaderStatsInline
                    onExpand={() => handleHeaderMinimizeChange(false)}
                  />
                  <AssetStatsInline
                    onExpand={() => handleHeaderMinimizeChange(false)}
                  />
                </>
              )}
              <Typography
                variant="caption"
                sx={{ color: theme.text.secondary }}
              >
                {traderName} -{" "}
                {t("traderDashboard.activeStrategies", { count })}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  loading ? (
                    <CircularProgress size={14} sx={{ color: theme.accent }} />
                  ) : (
                    <RefreshIcon />
                  )
                }
                onClick={refreshStrategies}
                disabled={loading}
                sx={{
                  borderColor: theme.border,
                  color: theme.text.secondary,
                  fontSize: "0.75rem",
                  "&:hover": {
                    borderColor: theme.accent,
                    backgroundColor: "rgba(88, 166, 255, 0.1)",
                    color: theme.accent,
                  },
                }}
              >
                {t("traderDashboard.refresh")}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Header Statistics - BTC, USDT prices */}
        <HeaderStats
          isMinimized={isHeaderMinimized}
          onMinimizeChange={handleHeaderMinimizeChange}
        />

        {/* Asset Statistics - Exchange balances and position spent */}
        {!isHeaderMinimized && <AssetStats />}

        {/* Tabs for Positions and New Position - hidden when minimized (shown in header) */}
        {!isHeaderMinimized && (
          <Box
            sx={{
              px: { xs: 2, sm: 3, md: 4 },
              borderBottom: `1px solid ${theme.border}`,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                "& .MuiTab-root": {
                  color: theme.text.secondary,
                  fontWeight: 500,
                  "&.Mui-selected": {
                    color: theme.accent,
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: theme.accent,
                },
              }}
            >
              <Tab
                icon={<ListAltIcon />}
                iconPosition="start"
                label={t("traderDashboard.tabs.activePositions", { count })}
              />
              <Tab
                icon={<AddCircleOutlineIcon />}
                iconPosition="start"
                label={t("traderDashboard.tabs.newPosition")}
              />
              <Tab
                icon={<HistoryIcon />}
                iconPosition="start"
                label={t("traderDashboard.tabs.history", {
                  count: completedCount,
                })}
              />
            </Tabs>
          </Box>
        )}

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mx: { xs: 2, sm: 3, md: 4 }, mb: 2, mt: 2 }}
          >
            {error}
          </Alert>
        )}

        {/* Tab 0: Active Positions */}
        <TabPanel value={activeTab} index={0}>
          {/* Loading State */}
          {loading && strategies.length === 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: "primary.main" }} />
            </Box>
          )}

          {/* Empty State */}
          {!loading && strategies.length === 0 && !error && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 8,
                color: "text.secondary",
              }}
            >
              <PlayArrowIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6">
                {t("traderDashboard.emptyState.noActiveStrategies")}
              </Typography>
              <Typography variant="body2">
                {t("traderDashboard.emptyState.startNewStrategy")}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => setActiveTab(1)}
                  sx={{
                    borderColor: theme.accent,
                    color: theme.accent,
                    "&:hover": {
                      borderColor: theme.accent,
                      backgroundColor: "rgba(88, 166, 255, 0.1)",
                    },
                  }}
                >
                  {t("traderDashboard.emptyState.createNewPosition")}
                </Button>
                <Tooltip
                  title={t("traderDashboard.registerStrategy.tooltip")}
                  arrow
                >
                  <Button
                    variant="outlined"
                    startIcon={<AppRegistrationIcon />}
                    onClick={handleOpenRegisterDialog}
                    sx={{
                      borderColor: theme.text.secondary,
                      color: theme.text.secondary,
                      "&:hover": {
                        borderColor: theme.accent,
                        color: theme.accent,
                        backgroundColor: "rgba(88, 166, 255, 0.1)",
                      },
                    }}
                  >
                    {t("traderDashboard.registerStrategy.buttonLabel")}
                  </Button>
                </Tooltip>
              </Stack>
            </Box>
          )}

          {/* Position List */}
          {strategies.length > 0 && (
            <Box
              sx={{
                mx: { xs: 1, sm: 2, md: 3 },
                mb: 4,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
              }}
            >
              {/* Filter Bar with Exchange Icons and Search */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1, px: 1 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <FilterListIcon
                    sx={{ color: theme.text.secondary, fontSize: 18 }}
                  />

                  {/* Exchange filter toggles with logos */}
                  <Tooltip title="Binance" arrow>
                    <IconButton
                      onClick={() => handleExchangeToggle("binance")}
                      size="small"
                      sx={{
                        p: 0.5,
                        opacity: exchangeFilters.binance ? 1 : 0.3,
                        border: `1px solid ${exchangeFilters.binance ? "#f3ba2f" : theme.border}`,
                        "&:hover": {
                          opacity: 1,
                          backgroundColor: "rgba(243, 186, 47, 0.1)",
                        },
                      }}
                    >
                      <Avatar
                        src={getExchangeLogo(3)}
                        sx={{ width: 18, height: 18 }}
                      />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Upbit" arrow>
                    <IconButton
                      onClick={() => handleExchangeToggle("upbit")}
                      size="small"
                      sx={{
                        p: 0.5,
                        opacity: exchangeFilters.upbit ? 1 : 0.3,
                        border: `1px solid ${exchangeFilters.upbit ? "#005aff" : theme.border}`,
                        "&:hover": {
                          opacity: 1,
                          backgroundColor: "rgba(0, 90, 255, 0.1)",
                        },
                      }}
                    >
                      <Avatar
                        src={getExchangeLogo(0)}
                        sx={{ width: 18, height: 18 }}
                      />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Bithumb" arrow>
                    <IconButton
                      onClick={() => handleExchangeToggle("bithumb")}
                      size="small"
                      sx={{
                        p: 0.5,
                        opacity: exchangeFilters.bithumb ? 1 : 0.3,
                        border: `1px solid ${exchangeFilters.bithumb ? "#ff7a00" : theme.border}`,
                        "&:hover": {
                          opacity: 1,
                          backgroundColor: "rgba(255, 122, 0, 0.1)",
                        },
                      }}
                    >
                      <Avatar
                        src={getExchangeLogo(1)}
                        sx={{ width: 18, height: 18 }}
                      />
                    </IconButton>
                  </Tooltip>

                  {/* Count indicator */}
                  <Typography
                    variant="caption"
                    sx={{ color: theme.text.muted, ml: 1 }}
                  >
                    {sortedStrategies.length}/{strategies.length}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  {/* Register Strategy Button */}
                  <Tooltip
                    title={t("traderDashboard.registerStrategy.tooltip")}
                    arrow
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AppRegistrationIcon />}
                      onClick={handleOpenRegisterDialog}
                      sx={{
                        borderColor: theme.border,
                        color: theme.text.secondary,
                        fontSize: "0.75rem",
                        textTransform: "none",
                        "&:hover": {
                          borderColor: theme.accent,
                          backgroundColor: "rgba(88, 166, 255, 0.1)",
                          color: theme.accent,
                        },
                      }}
                    >
                      {t("traderDashboard.registerStrategy.buttonLabel")}
                    </Button>
                  </Tooltip>

                  <TextField
                    size="small"
                    placeholder={t("traderDashboard.searchPlaceholder")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{ color: theme.text.secondary, fontSize: 18 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: { xs: "100%", sm: 200 },
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: theme.bg.secondary,
                        "& fieldset": {
                          borderColor: theme.border,
                        },
                        "&:hover fieldset": {
                          borderColor: theme.accent,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: theme.accent,
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        color: theme.text.primary,
                        fontSize: "0.85rem",
                        py: 0.75,
                        "&::placeholder": {
                          color: theme.text.secondary,
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Stack>
              </Stack>

              {/* Table */}
              <Box
                sx={{
                  backgroundColor: theme.bg.secondary,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 1,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                {/* Bulk Action Toolbar */}
                <BulkActionToolbar
                  selectedIds={selectedStrategyIds}
                  totalCount={sortedStrategies.length}
                  onClearSelection={handleClearSelection}
                  onSelectAll={handleSelectAll}
                  onBulkPause={bulkPause}
                  onBulkResume={bulkResume}
                  onBulkReload={bulkReload}
                  onOpenBudgetDialog={handleOpenBudgetDialogFromToolbar}
                  onOpenProfitTiersDialog={
                    handleOpenProfitTiersDialogFromToolbar
                  }
                  onOpenHedgeConfigDialog={
                    handleOpenHedgeConfigDialogFromToolbar
                  }
                />

                {/* Header Row */}
                <PositionListHeader
                  totalFundingRate={totalFundingRate}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  t={t}
                />

                {/* Data Rows */}
                <Box sx={{ overflowY: "auto", flex: 1, minHeight: 0, pb: 6 }}>
                  {sortedStrategies.map((strategy) => (
                    <PositionRow
                      key={strategy.strategy_id}
                      strategy={strategy}
                      onAction={handleStrategyAction}
                      loadingAction={
                        actionLoadingMap[strategy.strategy_id] || null
                      }
                      onHoverStart={(strategyId) =>
                        handleRowHoverStart(strategyId, currentSortOrder)
                      }
                      onHoverEnd={handleRowHoverEnd}
                      isHovered={hoveredStrategyId === strategy.strategy_id}
                      isSelected={selectedStrategyIds.has(strategy.strategy_id)}
                      onSelectionChange={handleSelectionChange}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </TabPanel>

        {/* Tab 1: New Position Opportunities */}
        <TabPanel value={activeTab} index={1}>
          <Box
            sx={{
              mx: { xs: 1, sm: 2, md: 3 },
              mb: 4,
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            <PositionOpportunitiesTable
              onLaunchStrategy={handleLaunchStrategy}
              activePositionBases={activePositionBases}
            />
          </Box>
        </TabPanel>

        {/* Tab 2: Completed Strategies History */}
        <TabPanel value={activeTab} index={2}>
          <Box
            sx={{
              mx: { xs: 1, sm: 2, md: 3 },
              mb: 4,
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            <CompletedStrategiesTable />
          </Box>
        </TabPanel>

        {/* Clear Position Confirmation Dialog */}
        <ClearPositionDialog
          open={clearDialogOpen}
          strategy={selectedStrategy}
          onClose={handleCloseDialog}
          onConfirm={handleConfirmClear}
          loading={clearLoading}
        />

        {/* Update Budget Dialog */}
        <UpdateBudgetDialog
          open={budgetDialogOpen}
          strategy={budgetDialogStrategy}
          onClose={handleCloseBudgetDialog}
          onConfirm={handleConfirmBudget}
          loading={budgetLoading}
        />

        {/* Pause Confirmation Dialog */}
        <PauseConfirmDialog
          open={pauseDialogOpen}
          strategy={pauseDialogStrategy}
          onClose={handleClosePauseDialog}
          onConfirm={handleConfirmPause}
          loading={pauseLoading}
        />

        {/* Skip BuyOpen Confirmation Dialog */}
        <SkipConfirmDialog
          open={skipDialogOpen}
          strategy={skipDialogStrategy}
          onClose={handleCloseSkipDialog}
          onConfirm={handleConfirmSkip}
          loading={skipLoading}
        />

        {/* Update Profit Tiers Dialog */}
        <UpdateProfitTiersDialog
          open={profitTiersDialogOpen}
          strategy={profitTiersDialogStrategy}
          onClose={handleCloseProfitTiersDialog}
          onConfirm={handleConfirmProfitTiers}
          loading={profitTiersLoading}
        />

        {/* Update Hedge Config Dialog */}
        <UpdateHedgeConfigDialog
          open={hedgeConfigDialogOpen}
          strategy={hedgeConfigDialogStrategy}
          onClose={handleCloseHedgeConfigDialog}
          onConfirm={handleConfirmHedgeConfig}
          loading={hedgeConfigLoading}
        />

        {/* Bulk Update Budget Dialog */}
        <UpdateBudgetDialog
          open={bulkBudgetDialogOpen}
          strategy={null}
          onClose={handleCloseBulkBudgetDialog}
          onConfirm={handleConfirmBulkBudget}
          loading={bulkBudgetLoading}
          title={`Update Budget (${selectedStrategyIds.size} strategies)`}
          selectedStrategies={selectedStrategiesInfo}
        />

        {/* Bulk Update Profit Tiers Dialog */}
        <UpdateProfitTiersDialog
          open={bulkProfitTiersDialogOpen}
          strategy={null}
          onClose={handleCloseBulkProfitTiersDialog}
          onConfirm={handleConfirmBulkProfitTiers}
          loading={bulkProfitTiersLoading}
          title={`Update Profit Tiers (${selectedStrategyIds.size} strategies)`}
          selectedStrategies={selectedStrategiesInfo}
        />

        {/* Bulk Update Hedge Config Dialog */}
        <UpdateHedgeConfigDialog
          open={bulkHedgeConfigDialogOpen}
          strategy={null}
          onClose={handleCloseBulkHedgeConfigDialog}
          onConfirm={handleConfirmBulkHedgeConfig}
          loading={bulkHedgeConfigLoading}
          title={t("traderDashboard.updateHedgeConfig.bulkTitle", {
            count: selectedStrategyIds.size,
          })}
          selectedStrategies={selectedStrategiesInfo}
        />

        {/* Strategy Execution Dialog - Now with live orderbook subscriptions */}
        {selectedOpportunity && (
          <StandbyHedgedConfirmDialog
            open={strategyDialogOpen}
            onClose={handleCancelStrategyDialog}
            onConfirm={handleExecuteStrategy}
            exchangeName={
              selectedOpportunity.spotExchange === Exchange.Upbit
                ? "업비트"
                : selectedOpportunity.spotExchange === Exchange.Bithumb
                  ? "빗썸"
                  : "바이낸스"
            }
            exchangeIndex={selectedOpportunity.spotExchangeIndex}
            quoteCurrency={
              selectedOpportunity.spotQuote === "KRW"
                ? "K"
                : selectedOpportunity.spotQuote === "BTC"
                  ? "B"
                  : "U"
            }
            baseAsset={selectedOpportunity.baseAsset}
            traderName={traderName || ""}
            traderRouteVersion="v1"
            buyOpenSpendInputs={buyOpenSpendInputs}
          />
        )}

        {/* Register Strategy Dialog */}
        <RegisterStrategyDialog
          open={registerDialogOpen}
          traderName={traderName || ""}
          onClose={handleCloseRegisterDialog}
          onConfirm={handleRegisterStrategy}
          loading={registerLoading}
          error={registerError}
        />

        {/* Strategy Detail Dialog */}
        <StrategyDetailDialog
          open={detailDialogOpen}
          strategyId={detailStrategyId}
          onClose={handleCloseDetailDialog}
        />
      </Box>
    </Box>
  );
};

/**
 * Dashboard with NewPositionData context wrapper
 * This allows access to opportunities data from both tabs
 */
const DashboardWithPositionData: React.FC = () => {
  // Tab state - lifted to control NewPositionDataProvider polling
  const [activeTab, setActiveTab] = useState(0);
  const isNewPositionTabActive = activeTab === 1;

  return (
    <NewPositionDataProvider
      enabled={isNewPositionTabActive}
      pollingInterval={3000}
    >
      <DashboardContent activeTab={activeTab} setActiveTab={setActiveTab} />
    </NewPositionDataProvider>
  );
};

/**
 * Trader Dashboard Page
 * Wrapped with TraderDataProvider and NewPositionDataProvider
 * for comprehensive data fetching with server-side polling
 */
const TraderDashboardPage: React.FC = () => {
  const { trader_name } = useParams<{ trader_name: string }>();

  if (!trader_name) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" sx={{ color: "error.main" }}>
          Trader name is required
        </Typography>
      </Box>
    );
  }

  return (
    <TraderDataProvider traderName={trader_name}>
      <DashboardWithPositionData />
    </TraderDataProvider>
  );
};

export default TraderDashboardPage;
